import { useState } from 'react';
import { ConfirmDialog } from '../ui';
import { quizApi, apiErrorMessage } from '../../services/api';
import { useOrg } from '../../context/OrgContext';
import { useToast } from '../../context/ToastContext';
import EditQuizDialog from './EditQuizDialog';

// Runs one of the things an owner can do to a launched test: 'close', 'edit'
// or 'delete'. Pages decide which to offer (utils/quizActions.js) and what to
// do afterwards; `onDone(action)` fires once the server has agreed.
export default function QuizActionDialog({ action, quiz, attemptCount = null, onCancel, onDone }) {
  const org = useOrg();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  if (action === 'edit') {
    return (
      <EditQuizDialog
        quiz={quiz}
        attemptCount={attemptCount}
        onClose={onCancel}
        onSaved={() => onDone('edit')}
      />
    );
  }

  const closing = action === 'close';

  const run = async () => {
    setBusy(true);
    try {
      if (closing) {
        await quizApi.close(org.id, quiz.id);
        toast.success(`${quiz.name} closed.`);
      } else {
        await quizApi.remove(org.id, quiz.id);
        toast.success(`${quiz.name} deleted.`);
      }
      onDone(action);
    } catch (err) {
      toast.error(apiErrorMessage(err, closing ? 'Failed to close test' : 'Failed to delete test'));
    } finally {
      setBusy(false);
    }
  };

  return closing ? (
    <ConfirmDialog
      title="Close this test now?"
      message={`Nobody can start “${quiz.name}” after this. Students already taking it can still finish and submit. To reopen it later, edit its close time.`}
      confirmLabel="Close now"
      busy={busy}
      onConfirm={run}
      onCancel={onCancel}
    />
  ) : (
    <ConfirmDialog
      title="Delete this test?"
      message={`“${quiz.name}” will disappear for every student in the batch. Nobody has started it, so no results are lost. This cannot be undone.`}
      confirmLabel="Delete test"
      busy={busy}
      onConfirm={run}
      onCancel={onCancel}
    />
  );
}
